import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Users, Coins, Banknote, Cpu, Mail, ArrowRight, AlertTriangle, FileDown, Plus } from "lucide-react";
import { adminApi } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { fmtMoney, fmtNum, fmtDate, Trend, StatusPill, Panel, PageHeader, Th, Td, Empty } from "@/components/admin/parts";

const MetricCard = ({ icon: Icon, label, value, extra, testId }) => (
  <div data-testid={testId} className="rounded-panel border border-brand-line bg-brand-surface p-5">
    <div className="flex items-center justify-between">
      <span className="flex h-9 w-9 items-center justify-center rounded-field bg-brand-elevated text-brand-accent">
        <Icon size={17} strokeWidth={2} />
      </span>
      {extra}
    </div>
    <p className="mt-4 font-display text-2xl font-bold text-brand-text">{value}</p>
    <p className="mt-0.5 text-xs text-brand-muted">{label}</p>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    adminApi.overview(days).then(setData).catch(() => {});
  }, [days]);

  if (!data) return <div className="text-brand-muted">Ładowanie…</div>;
  const m = data.metrics;

  return (
    <div>
      <PageHeader
        title="Pulpit administratora"
        subtitle="Przegląd kluczowych metryk i zdarzeń"
        action={
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-10 rounded-field border border-brand-line bg-brand-surface px-3 text-sm text-brand-text outline-none"
          >
            <option value={7}>Ostatnie 7 dni</option>
            <option value={30}>Ostatnie 30 dni</option>
            <option value={90}>Ostatnie 90 dni</option>
          </select>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <MetricCard testId={ADMIN.metricUsers} icon={Users} label="Użytkownicy" value={fmtNum(m.users.value)} extra={<Trend value={m.users.trend} />} />
        <MetricCard testId={ADMIN.metricCredits} icon={Coins} label="Sprzedane kredyty" value={fmtNum(m.soldCredits.value)} extra={<Trend value={m.soldCredits.trend} />} />
        <MetricCard testId={ADMIN.metricRevenue} icon={Banknote} label="Przychód brutto" value={fmtMoney(m.grossRevenue.value)} extra={<Trend value={m.grossRevenue.trend} />} />
        <MetricCard testId={ADMIN.metricAiCost} icon={Cpu} label="Koszt AI (szac.)" value={fmtMoney(m.aiCost.value)} extra={<span className="text-xs font-semibold text-brand-accent">{m.aiCost.margin}%</span>} />
        <MetricCard testId={ADMIN.metricMessages} icon={Mail} label="Wymagają odpowiedzi" value={fmtNum(m.newMessages.value)} extra={<span className="text-xs text-brand-muted">Nowe</span>} />
      </div>

      <p className="mt-3 text-xs text-brand-muted" data-testid="dashboard-margin-note">
        {m.aiCost.draft
          ? "Szacowana marża przed kosztami stałymi · dane robocze do czasu podpisania umowy Stripe."
          : "Szacowana marża przed kosztami stałymi."}
        {" "}Koszt szacowany — dostawca nie zwraca rzeczywistego zużycia tokenów.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
        <Panel title="Przychód i koszt AI">
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.series} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: "#7E807B", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                <YAxis tick={{ fill: "#7E807B", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#141414", border: "1px solid #262626", borderRadius: 10, fontSize: 12 }} labelStyle={{ color: "#f2f0ea" }} />
                <Line type="monotone" dataKey="revenue" name="Przychód (zł)" stroke="#B6FF00" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="aiCost" name="Koszt AI (zł)" stroke="#7E807B" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Najczęstsze operacje">
          {data.topOperations.items.length === 0 ? (
            <Empty>Brak operacji AI w tym okresie.</Empty>
          ) : (
            <ul className="space-y-3">
              {data.topOperations.items.map((op) => (
                <li key={op.actionKey}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-brand-text">{op.label}</span>
                    <span className="font-semibold text-brand-sec">{op.percent}%</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-brand-elevated">
                    <div className="h-full rounded-full bg-brand-accent" style={{ width: `${op.percent}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
        <Panel title="Ostatnie zdarzenia">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px]">
              <thead><tr className="border-b border-brand-line">
                <Th>Data</Th><Th>Użytkownik</Th><Th>Zdarzenie</Th><Th className="text-right">Kredyty</Th><Th>Status</Th>
              </tr></thead>
              <tbody>
                {data.recentEvents.map((e) => (
                  <tr key={e.id} className="border-b border-brand-line/60">
                    <Td className="whitespace-nowrap text-brand-sec">{fmtDate(e.created_at)}</Td>
                    <Td className="text-brand-sec">{e.user_email}</Td>
                    <Td>{e.event}</Td>
                    <Td className={`text-right font-semibold ${e.credits >= 0 ? "text-brand-accent" : "text-brand-sec"}`}>{e.credits > 0 ? "+" : ""}{e.credits}</Td>
                    <Td><StatusPill status={e.status} /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Wymaga uwagi">
          <div className="space-y-2">
            <Link to="/admin/wiadomosci" className="flex items-center gap-3 rounded-field border border-brand-line px-3 py-3 text-sm text-brand-text hover:border-brand-accent/50">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-elevated text-brand-accent"><Mail size={15} /></span>
              <span className="flex-1">{data.attention.newMessages} nowych wiadomości</span>
              <ArrowRight size={15} className="text-brand-muted" />
            </Link>
            <Link to="/admin/operacje-ai?status=failed" className="flex items-center gap-3 rounded-field border border-brand-line px-3 py-3 text-sm text-brand-text hover:border-brand-accent/50">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-elevated text-amber-300"><AlertTriangle size={15} /></span>
              <span className="flex-1">{data.attention.failedAiOps} nieudanych operacji AI</span>
              <ArrowRight size={15} className="text-brand-muted" />
            </Link>
            <Link to="/admin/platnosci" className="flex items-center gap-3 rounded-field border border-brand-line px-3 py-3 text-sm text-brand-text hover:border-brand-accent/50">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-elevated text-sky-300"><FileDown size={15} /></span>
              <span className="flex-1">{data.attention.paymentsToCheck} płatności do sprawdzenia</span>
              <ArrowRight size={15} className="text-brand-muted" />
            </Link>
            <Link to="/admin/uzytkownicy" className="flex items-center gap-3 rounded-field bg-brand-accent px-3 py-3 text-sm font-semibold text-brand-on">
              <Plus size={16} /> Dodaj korektę kredytów
            </Link>
          </div>
        </Panel>
      </div>
    </div>
  );
}
